/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { MyButton } from "../../component/Component";
import { AiOutlineLoading } from "react-icons/ai";
import Service from "../../../../api/service";
import { getContentMasterSearchParams } from ".";
import { SeachContent } from "./helpers";

const Overview = () => {
  // Original logic/state (unchanged)
  const [searchLoading, setSearchLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [localSearch, setLocalSearch] = useState(null);

  // Optional UI filters (pure client-side; no backend changes)
  const [sectionFilter, setSectionFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All"); // All | Active | Suspended
  const [sortBy, setSortBy] = useState("Relevance"); // Relevance | Title A–Z | Title Z–A
  const [showWithResultsOnly, setShowWithResultsOnly] = useState(true);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (keyword && keyword !== "") {
      getSearch();
    }
  };

  const getSearch = async () => {
    const url = `/api/v1/search/?search_key=${keyword}&origin=admin`;
    setSearchLoading(true);
    const { data } = await Service.get(url);
    setSearchLoading(false);
    if (data) setLocalSearch(data);
  };

  let totalResult = 0;
  if (localSearch) {
    for (const key in localSearch) {
      if (!["Company", "FirstLevel", "SecondLevel", "ThirdLevel", "FourthLevel"].includes(key)) {
        totalResult += localSearch[key]?.length;
      }
    }
  }

  // Section options derived from current result
  const sectionOptions = ["All"].concat(
    (localSearch ? getContentMasterSearchParams(localSearch) : [])
      ?.map((c) => c?.title)
      ?.filter(Boolean)
  );

  // Client-side filter/sort for each section list
  const applyFiltersAndSort = (arr = [], titleLabel = "title") => {
    let items = Array.isArray(arr) ? [...arr] : [];
    if (statusFilter !== "All") {
      const want = statusFilter === "Active" ? 1 : 0;
      items = items.filter((i) => (typeof i?.status === "number" ? i.status === want : true));
    }
    if (sortBy === "Title A–Z") {
      items.sort((a, b) => String(a?.[titleLabel] ?? a?.title ?? "")
        .localeCompare(String(b?.[titleLabel] ?? b?.title ?? ""), undefined, { sensitivity: "base" }));
    } else if (sortBy === "Title Z–A") {
      items.sort((a, b) => String(b?.[titleLabel] ?? b?.title ?? "")
        .localeCompare(String(a?.[titleLabel] ?? a?.title ?? ""), undefined, { sensitivity: "base" }));
    }
    return items;
  };

  const clearAll = () => {
    setKeyword("");
    setLocalSearch(null);
  };

  const setExample = (text) => setKeyword(text);

  return (
    <div className="p-3 lg:p-4 h-full overflow-y-auto rounded-b-xl bg-gray-50">
      <div className="mx-auto w-full max-w-6xl space-y-4">
        {/* Search Card */}
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          {/* Label + helper */}
          <div className="mb-3 flex flex-col items-center text-center">
            <h2 className="text-sm font-semibold text-gray-900">Search Content</h2>
            <p className="mt-1 max-w-2xl text-[11px] text-gray-600">
              Tip: Use clear keywords like <span className="font-semibold">“safari”</span>,{" "}
              <span className="font-semibold">“Nairobi hotels”</span>, or{" "}
              <span className="font-semibold">“music festival”</span>. You can filter by section, status,
              and sort results below.
            </p>
          </div>

          {/* Search row */}
          <div className="w-full h-full flex justify-center items-center">
            <form onSubmit={handleSearch} className="flex w-full max-w-2xl">
              {/* Input with left icon + inline clear */}
              <div className="relative w-full">

                <input
                  type="text"
                  placeholder="Search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full rounded-l-lg border border-r-0 border-gray-200 p-3 pr-9 text-xs font-medium outline-none placeholder:text-xs md:min-w-[250px] lg:min-w-[350px] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                />
                {keyword ? (
                  <button
                    type="button"
                    aria-label="Clear text"
                    onClick={() => setKeyword("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                  >
                    ×
                  </button>
                ) : null}
              </div>

              {/* Search button */}
              <MyButton
                type="submit"
                onClick={() => setSearchLoading(true)}
                className="flex w-[120px] items-center justify-center space-x-2 rounded-r-lg border-2 border-gray-900 bg-gray-900 px-4 text-xs font-semibold text-white hover:bg-gray-800"
              >
                {searchLoading ? (
                  <AiOutlineLoading className="animate-spin text-base" />
                ) : (
                  <span>Search</span>
                )}
              </MyButton>

              {/* Clear All (clears input & results) */}
              <button
                type="button"
                onClick={clearAll}
                className="ml-2 inline-flex min-w-fit items-center justify-center rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                Clear All
              </button>
            </form>
          </div>

          {/* Example chips */}
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            {["safari", "Nairobi hotels", "music festival", "restaurant", "city guide"].map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => setExample(ex)}
                className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-100"
              >
                {ex}
              </button>
            ))}
          </div>

          {localSearch ? (
            <div className="pt-5 text-center">
              <p className="text-xs text-gray-600">
                About <span className="font-semibold text-gray-900">{totalResult}</span> Results Found.
              </p>
            </div>
          ) : null}
        </div>

        {/* Filters (UI-only; optional) */}
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {/* Section */}
            <div className="flex flex-col">
              <label className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                Section
              </label>
              <select
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
                className="rounded-lg border border-gray-200 bg-white p-2.5 text-xs focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              >
                {sectionOptions.map((opt, i) => (
                  <option key={i} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            {/* Status */}
            <div className="flex flex-col">
              <label className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-lg border border-gray-200 bg-white p-2.5 text-xs focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              >
                <option>All</option>
                <option>Active</option>
                <option>Suspended</option>
              </select>
            </div>
            {/* Sort */}
            <div className="flex flex-col">
              <label className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                Sort
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-lg border border-gray-200 bg-white p-2.5 text-xs focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              >
                <option>Relevance</option>
                <option>Title A–Z</option>
                <option>Title Z–A</option>
              </select>
            </div>
            {/* Toggle */}
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={showWithResultsOnly}
                  onChange={(e) => setShowWithResultsOnly(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                Show sections with results only
              </label>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="w-full">
          <div className="h-full w-full pt-2 lg:w-2/3 xl:w-1/2 mx-auto">
            {localSearch ? (
              <div className="flex h-full w-full flex-col space-y-5">
                {getContentMasterSearchParams(localSearch)
                  ?.filter((content) => (sectionFilter === "All" ? true : content?.title === sectionFilter))
                  ?.map((content, key) => {
                    const filteredData = applyFiltersAndSort(
                      content?.data,
                      content?.titleLabel || "title"
                    );
                    if (showWithResultsOnly && (!filteredData || filteredData.length === 0)) return null;

                    return (
                      <SeachContent
                        key={key}
                        data={filteredData}
                        route={content?.route}
                        title={content?.title}
                        idLabel={content?.idLabel}
                        setLocalSearch={setLocalSearch}
                        titleLabel={content?.titleLabel}
                        collection={content.collection}
                        type={content.type}
                        pushMethod={content.pushMethod}
                        onDeleteAction={getSearch}
                      />
                    );
                  })}
              </div>
            ) : (
              <div className="mx-auto mt-6 rounded-xl border border-dashed border-gray-200 bg-white p-8 text-center text-xs text-gray-600 shadow-sm">
                Your search results will show here.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
